// src/pages/Faq.js
// v3 – public FAQ copy aligned with current product, school-owned workflow, backups, and notifications

import React from "react";
import { Helmet } from "react-helmet";
import "../styles/Privacy.css";

const Faq = () => {
  return (
    <div className="privacy-page page">
      <Helmet>
        <title>Security, Privacy &amp; School Workflow FAQ - GhrianGhraffy</title>
        <meta
          name="description"
          content="Security, privacy and school workflow FAQ for GhrianGhraffy — answers for Irish schools and parents about school photo access, principal review, GDPR, website publishing, Instagram posting, notifications, backups, and account deletion."
        />
      </Helmet>

      <div className="privacy-card">
        <header className="privacy-header">
          <p className="privacy-kicker">FAQ</p>
          <h1 className="privacy-title">Security, Privacy &amp; School Workflow FAQ</h1>
          <p className="privacy-meta">Last updated: June 19, 2026</p>
        </header>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Who is GhrianGhraffy for?</h2>
          <p>
            GhrianGhraffy is built for schools that want a clearer way to capture,
            review, organise, and share school photos and videos.
          </p>
          <p>
            Principals and authorised staff can use GhrianGhraffy to upload school
            media. Principals keep oversight through review and school-level settings.
            Parents can view approved content that their school has made available.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Is GhrianGhraffy mainly for Irish schools?</h2>
          <p>
            GhrianGhraffy is designed with Irish primary schools in mind, including
            school roll numbers, principal-led setup, staff invitations, parent access,
            and school-controlled photo approval.
          </p>
          <p>
            The service is operated from Ireland and is designed around GDPR-aware
            school workflows, but each school remains responsible for its own policies,
            consent processes, and decisions about pupil images.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Who approves photos before they are shared?</h2>
          <p>
            The intended workflow is principal-led. Staff can capture and upload school
            moments, but content is reviewed before it is made visible to parents or
            optional publishing destinations.
          </p>
          <p>
            A Principal can also nominate authorised staff to help with photo review.
            Review helper access is school-scoped and should only be given to staff the
            school trusts to make review decisions.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Can parents download or screenshot photos?</h2>
          <p>
            Like any service that displays photos or videos, screenshots and screen
            recordings cannot be completely prevented at a technical level.
          </p>
          <p>
            GhrianGhraffy reduces risk by avoiding public discovery, using role-based
            access, limiting access to authorised users, and keeping school sharing
            decisions inside a controlled workflow.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Are school photos publicly visible on the internet?</h2>
          <p>
            No. GhrianGhraffy does not provide a public search page for school media,
            and app media is not intended to be exposed through permanent public links.
          </p>
          <p>
            If a school chooses to publish selected approved items on its own website,
            only content enabled for that destination is shown, and media is delivered
            using controlled, time-limited access links.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Do we have to use the website or Instagram features?</h2>
          <p>
            No. Website publishing and Instagram posting are optional. A school can use
            the core GhrianGhraffy workflow for staff uploads, Principal review, and
            parent access without connecting either destination.
          </p>
          <p>
            Schools can review optional setup later and decide whether website or
            Instagram publishing is appropriate for their own policies and community.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How is this different from shared folders, email, or chat apps?</h2>
          <p>
            Shared folders, email attachments, and messaging apps can make it hard for a
            school to keep track of where photos have gone, who has access, and whether
            links or copies remain available later.
          </p>
          <p>
            GhrianGhraffy is designed around a school-managed workflow: staff upload,
            Principals review, and parents see approved content through school-scoped
            access.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Do photos get stored on teachers’ phones?</h2>
          <p>
            GhrianGhraffy is designed to reduce long-term storage of school media on
            personal devices. Working copies may exist temporarily during capture,
            preview, upload, retry, or cancellation.
          </p>
          <p>
            Photos or videos already saved in a device’s own camera roll remain under
            that device user’s control.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Who can see our school’s photos?</h2>
          <p>
            Access is role-based and scoped to a school. Principals and authorised staff
            can manage or review school content according to their role. Parents can view
            approved content made available to their school community.
          </p>
          <p>
            Schools remain responsible for deciding what should be captured, approved,
            published, removed, or restricted under their own policies.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How do parent accounts work?</h2>
          <p>
            Parent access is school-controlled. Parents or guardians should only be able
            to view school content after they have been invited or authorised through the
            school’s GhrianGhraffy workflow.
          </p>
          <p>
            This is intended to avoid public discovery of school media and to keep access
            tied to the school community rather than open internet browsing.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Why might a parent be asked for a profile photo?</h2>
          <p>
            A school may require a parent or guardian to add a profile photo before
            viewing school media. This gives the school an extra account-verification
            signal and helps reduce the risk of unknown or mistaken access.
          </p>
          <p>
            Profile photos are part of the school-controlled account workflow and should
            be used only for appropriate verification and account-management purposes.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">What happens when a staff member leaves?</h2>
          <p>
            A school administrator can revoke staff access. Revoking access prevents the
            former staff member from continuing to use school features, without deleting
            the school’s existing content.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Can staff help with photo review?</h2>
          <p>
            Yes, where the Principal chooses to allow it. GhrianGhraffy supports a
            delegated review-helper workflow so trusted staff can help review photos for
            the same school.
          </p>
          <p>
            Delegated review access should be treated as a school governance decision.
            Schools should only give that access to staff members who understand the
            school’s photo, consent, and safeguarding expectations.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How does GhrianGhraffy support GDPR compliance?</h2>
          <p>
            GhrianGhraffy supports GDPR-aligned school workflows through role-based
            access, review controls, data minimisation, school-scoped visibility,
            deletion and revocation mechanisms, and security safeguards.
          </p>
          <p>
            In many school use cases, the school is the data controller for pupil photos,
            videos, captions, class labels, and related school content. GhrianGhraffy
            generally acts as a data processor for that school-controlled content.
          </p>
          <p>
            This FAQ is not legal advice. Schools should follow their own policies and
            seek appropriate advice where needed, especially for pupil-image consent,
            publication, retention, and removal decisions.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Does GhrianGhraffy sell data or show ads?</h2>
          <p>
            No. GhrianGhraffy does not sell personal data, does not show advertising in
            the app, and does not use school content for advertising.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Does GhrianGhraffy use analytics or crash-reporting tools?</h2>
          <p>
            GhrianGhraffy does not currently use third-party analytics or crash-reporting
            tools in the app. If that changes, the Privacy Policy should be updated to
            explain what is used and why.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Does GhrianGhraffy send push notifications?</h2>
          <p>
            GhrianGhraffy may use push notifications where a user enables them, such as
            reminders or digest notifications for Principals about photos waiting for
            review.
          </p>
          <p>
            Push notifications require a device permission and may involve notification
            tokens and delivery services such as Expo Push Notifications and Google or
            Firebase Cloud Messaging. Users can manage notification permissions through
            their device settings.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How does website publishing work?</h2>
          <p>
            If a school chooses to show approved content on its own website, the website
            integration requests school-approved items from GhrianGhraffy through
            controlled server-side access.
          </p>
          <p>
            Website publishing is optional. A school can choose whether website sharing
            is enabled and which approved content is allowed to appear there.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Can schools publish to Instagram?</h2>
          <p>
            Instagram publishing is optional and only applies where a school chooses to
            connect an official Instagram account and publish selected approved content.
          </p>
          <p>
            Schools should make sure they have the appropriate permission or lawful basis
            before publishing pupil photos or videos to external destinations.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Where is our data stored and backed up?</h2>
          <p>
            GhrianGhraffy stores account, school setup, media, and service data on its
            servers and serves the service over HTTPS. Operational backups may also be
            stored offsite so the service can be restored after data loss or server
            failure.
          </p>
          <p>
            Backup copies are intended for service continuity and disaster recovery, not
            for public access. Deleted data may remain in backups for a limited period
            until those backups expire or are rotated.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">What happens at the end of a trial or subscription?</h2>
          <p>
            GhrianGhraffy subscriptions are school-owned. A Principal may manage billing
            for the school, but the subscription and upload access belong to the school
            rather than to one individual user.
          </p>
          <p>
            If a trial does not convert or a school stops using the service, access may
            be restricted and school-specific data may be deleted in line with the
            Privacy Policy, service terms, and any required billing or legal retention
            obligations.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How do we request deletion or changes?</h2>
          <p>
            Principals and school administrators can manage school content through their
            school workflow. For account deletion, data deletion, correction requests, or
            privacy questions, contact{" "}
            <a href="mailto:privacy@ghrianghraffy.com">privacy@ghrianghraffy.com</a>.
          </p>
          <p>
            If the request relates to pupil photos, videos, or school-controlled content,
            parents and guardians should normally contact the school first. GhrianGhraffy
            will work with the school where needed.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">What should a Principal check before using GhrianGhraffy?</h2>
          <p>
            GhrianGhraffy provides the technical workflow, but the school remains
            responsible for the school-level decisions about pupil images, publication,
            retention, and parent or guardian requests.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Faq;